import { gql } from '@apollo/client';

const fraudTokenFields = gql`
  fragment FraudTokenFields on FraudToken {
    id
    tokenType
    value
    active
    notes
    createdAt
    fraudDetectionsCount
    confirmedFraudCount
    falsePositiveCount
    unknownCount
    createdBy {
      id
      email
    }
  }
`;

const eventFields = gql`
  fragment FraudTokenEventFields on Event {
    id
    name
    status
    createdAt
    discardedAt
    markedFraudulentAt
    origin
    fraudDetections {
      id
      fraudToken {
        id
        tokenType
        value
      }
    }
    venue {
      id
      name
    }
    planner {
      id
      fullName
      email
      phone
    }
    messages {
      id
      plainTextFromHtml
      createdAt
      sender
    }
  }
`;

const fraudDetectionFields = gql`
  fragment FraudDetectionFields on FraudDetection {
    id
    detectedAt
    outcome
    matchType
    matchedValue
    event {
      ...FraudTokenEventFields
    }
  }
  ${eventFields}
`;

export const FRAUD_TOKENS_QUERY = gql`
  query FraudTokens($tokenType: FraudTokenTypeEnum, $active: Boolean) {
    fraudTokens(tokenType: $tokenType, active: $active) {
      ...FraudTokenFields
    }
  }
  ${fraudTokenFields}
`;

export const CREATE_FRAUD_TOKEN_MUTATION = gql`
  mutation CreateFraudToken(
    $tokenType: FraudTokenTypeEnum!
    $value: String!
    $notes: String
  ) {
    createFraudToken(
      input: { tokenType: $tokenType, value: $value, notes: $notes }
    ) {
      fraudToken {
        ...FraudTokenFields
      }
      errors {
        message
      }
    }
  }
  ${fraudTokenFields}
`;

export const UPDATE_FRAUD_TOKEN_MUTATION = gql`
  mutation UpdateFraudToken(
    $id: ID!
    $tokenType: FraudTokenTypeEnum
    $value: String
    $notes: String
    $active: Boolean
  ) {
    updateFraudToken(
      input: {
        id: $id
        tokenType: $tokenType
        value: $value
        notes: $notes
        active: $active
      }
    ) {
      fraudToken {
        ...FraudTokenFields
      }
      errors {
        message
      }
    }
  }
  ${fraudTokenFields}
`;

export const DELETE_FRAUD_TOKEN_MUTATION = gql`
  mutation DeleteFraudToken($id: ID!) {
    deleteFraudToken(input: { id: $id }) {
      fraudToken {
        id
      }
      errors {
        message
      }
    }
  }
`;

export const FRAUD_TOKEN_DETAILS_QUERY = gql`
  query FraudTokenDetails($id: ID!) {
    fraudTokenDetails(id: $id) {
      fraudToken {
        ...FraudTokenFields
        precision
        soloDetectionPercentage
        confirmedFraudCount
        falsePositiveCount
        unknownCount
        fraudDetectionsCount
        soloDetectionCount
        coDetectionCount
        falsePositiveDetections(limit: 100) {
          ...FraudDetectionFields
        }
        confirmedFraudDetections(limit: 100) {
          ...FraudDetectionFields
        }
        unknownDetections(limit: 100) {
          ...FraudDetectionFields
        }
      }
      events {
        ...FraudTokenEventFields
      }
    }
  }
  ${fraudTokenFields}
  ${eventFields}
  ${fraudDetectionFields}
`;

export const SUSPICIOUS_MESSAGE_REPORTS_QUERY = gql`
  query SuspiciousMessageReports($status: String) {
    suspiciousMessageReports(status: $status) {
      id
      status
      notes
      createdAt
      message {
        id
        plainTextFromHtml
      }
      event {
        ...FraudTokenEventFields
      }
      reportedBy {
        id
        email
      }
      fraudToken {
        id
        tokenType
        value
      }
    }
  }
  ${eventFields}
`;

export const IGNORE_SUSPICIOUS_MESSAGE_REPORT_MUTATION = gql`
  mutation IgnoreSuspiciousMessageReport($id: ID!, $notes: String) {
    ignoreSuspiciousMessageReport(input: { id: $id, notes: $notes }) {
      suspiciousMessageReport {
        id
        status
        notes
      }
      errors {
        message
      }
    }
  }
`;

export const CREATE_FRAUD_TOKEN_FROM_REPORT_MUTATION = gql`
  mutation CreateFraudTokenFromReport(
    $reportId: ID!
    $tokenType: String!
    $value: String!
    $notes: String
  ) {
    createFraudTokenFromReport(
      input: {
        reportId: $reportId
        tokenType: $tokenType
        value: $value
        notes: $notes
      }
    ) {
      fraudToken {
        id
        tokenType
        value
        notes
      }
      suspiciousMessageReport {
        id
        status
      }
      errors {
        message
      }
    }
  }
`;

export const PLANNER_FRAUD_ANALYSIS_QUERY = gql`
  query PlannerFraudAnalysis($plannerId: ID!) {
    plannerFraudAnalysis(plannerId: $plannerId) {
      planner {
        id
        fullName
        email
        phone
      }
      eventsWithMatchingEmail {
        ...FraudTokenEventFields
      }
      eventsWithMatchingPhone {
        ...FraudTokenEventFields
      }
    }
  }
  ${eventFields}
`;

export const PREVIEW_FRAUD_TOKEN_QUERY = gql`
  query PreviewFraudToken($tokenType: FraudTokenTypeEnum!, $value: String!) {
    previewFraudToken(tokenType: $tokenType, value: $value) {
      totalEvents
      confirmedFraudCount
      falsePositiveCount
      unknownCount
      precision
      sampleEvents {
        ...FraudTokenEventFields
      }
    }
  }
  ${eventFields}
`;
